import React, { useState, useEffect, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";

//Serivce
import ItemService from "../../../services/supplier_appCommonService";

//Components
import Layout from "../../../Layout/Layout";
import { InputField } from "../../../Components/formElements/InputField";
import { ComboboxField } from "../../../Components/formElements/ComboboxField";
import FileUploadComponent from "../../../Components/formElements/FileUploadComponent";
import FileViewerModal from "../../../Components/FileViewer/FileViewerModal";
import Constants from "../../../common/SupplierConstants";

//Company Creation shared options
import {
    companyTabList,
    getCompanyFormTemplate,
    companyTypeOptions,
    companyStatusOptions,
    countryOptions,
    stateOptions,
    currencyOptions,
    accountingPeriodOptions,
    erpSystemOptions,
    enabledDisabledOptions,
    budgetTypeOptions,
    budgetControlLevelOptions,
    budgetCheckBasisOptions,
    budgetExceedActionOptions,
    budgetApprovalWorkflowOptions,
} from "./companyConstants";

const CompanyCreation = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Update mode - companyId passed via navigation state or query param
    const companyId = location?.state?.companyId || new URLSearchParams(location.search).get("id");
    const isUpdateMode = !!companyId;

    /* Tabs (keyed by tab-pane id) that currently contain a validation error, so
       the tab header can be highlighted red - same pattern as CreateRFQTabs */
    const [tabErrors, setTabErrors] = useState({});
    // Currently active tab id - controls which tab-pane is visible.
    const [activeTab, setActiveTab] = useState("basic-info");
    const [getFormData, setFormData] = useState(getCompanyFormTemplate());
    const [getErrorFields, setErrorFields] = useState([]);
    const [getIsLoading, setIsLoading] = useState(false);

    // Accordion card visibility - one per section (same as RFQ create page)
    const [isCardVisible, setCardVisible] = useState(true);
    const [isCardVisible2, setCardVisible2] = useState(true);
    const [isCardVisible3, setCardVisible3] = useState(true);
    const [isCardVisible4, setCardVisible4] = useState(true);

    // File viewer
    const [getShowFileViewModal, setShowFileViewModal] = useState(false);
    const [getFileViewData, setFileViewData] = useState("");

    const toggleCardVisibility = () => setCardVisible(!isCardVisible);
    const toggleCardVisibility2 = () => setCardVisible2(!isCardVisible2);
    const toggleCardVisibility3 = () => setCardVisible3(!isCardVisible3);
    const toggleCardVisibility4 = () => setCardVisible4(!isCardVisible4);

    /* ---------------------------- Load Company Detail in update mode --------------------------------*/
    useEffect(() => {
        if (companyId) {
            getCompanyDetailData(companyId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    const getCompanyDetailData = async (id) => {
        try {
            await ItemService.getCompanyDetail({ _id: id }).then((item) => {
                if (item?.status === 200 && item?.data) {
                    let detail = item.data;
                    setFormData({
                        ...getCompanyFormTemplate(),
                        ...detail,
                        _id: detail._id,
                    });
                } else {
                    toast.error(item?.message || "!! Unable to Get Company Detail");
                    navigate(Constants.COMPANY_CREATION_LIST);
                }
            });
        } catch (error) {
            console.log("getCompanyDetailData Failed:", error);
            toast.error("!! Unable to Get Company Detail");
        }
    };

    /* ---------------------------- Form Field Change --------------------------------*/
    const handleFieldChange = (e) => {
        let { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrorFields((prev) => prev.filter((field) => field !== name));
        setTabErrors((prev) => ({ ...prev, [getTabOfField(name)]: false }));
    };

    // Combobox value change - single stable handler keyed on the field's name prop
    const handleDropdownGetValue = useCallback((obj) => {
        if (!obj?.textField) return;
        setFormData((prev) => ({ ...prev, [obj.textField]: obj?.name || "" }));
        setTabErrors((prev) => ({ ...prev, [getTabOfField(obj.textField)]: false }));
    }, []);

    // Company Logo upload (optional) - FileUploadComponent callback
    const handleLogoUpload = (item) => {
        if (item?.attachmentId || item?.results) {
            let uploadedFile = item?.results;
            setFormData((prev) => ({
                ...prev,
                companyLogo: item?.attachmentId || "",
                companyLogoName:
                    uploadedFile?.originalFilename ||
                    uploadedFile?.fileName ||
                    uploadedFile?.name ||
                    "",
            }));
            toast.success("Company Logo uploaded successfully");
        } else {
            toast.error(item?.message || "!! Unable to upload Company Logo");
        }
    };

    // View attachment files (same pattern as RFQ create page)
    const handleViewFile = (fileAttachmentId) => {
        setFileViewData(fileAttachmentId);
        setShowFileViewModal(true);
    };
    const handleViewFileHide = () => {
        setShowFileViewModal(false);
    };

    /* ---------------------------- Validation --------------------------------*/
    const getTabOfField = (field) => {
        const tabFieldMap = {
            companyCode: "basic-info",
            companyName: "basic-info",
            legalName: "basic-info",
            companyShortName: "basic-info",
            companyType: "basic-info",
            registrationNumber: "basic-info",
            companyLogo: "basic-info",
            companyStatus: "basic-info",
            addressLine1: "address-contact",
            addressLine2: "address-contact",
            country: "address-contact",
            state: "address-contact",
            city: "address-contact",
            pinCode: "address-contact",
            primaryContactPerson: "address-contact",
            contactEmail: "address-contact",
            contactNumber: "address-contact",
            website: "address-contact",
            pan: "tax-financial",
            gstNumber: "tax-financial",
            currency: "tax-financial",
            financialYearStart: "tax-financial",
            financialYearEnd: "tax-financial",
            accountingPeriod: "tax-financial",
            erpSystem: "tax-financial",
            erpCompanyId: "tax-financial",
            budgetControl: "budget-control",
            budgetType: "budget-control",
            budgetControlLevel: "budget-control",
            budgetCheckBasis: "budget-control",
            budgetExceedAction: "budget-control",
            budgetCarryForward: "budget-control",
            budgetApprovalWorkflow: "budget-control",
            defaultBudgetOwner: "budget-control",
            budgetTolerancePercent: "budget-control",
        };
        return tabFieldMap[field] || "basic-info";
    };

    const validateForm = () => {
        let errors = {};
        let errorTabs = {};

        // Mandatory fields
        if (!getFormData.companyName?.trim()) {
            errors.companyName = "Company Name is mandatory";
        }
        if (!getFormData.companyType) {
            errors.companyType = "Company Type is mandatory";
        }
        if (!getFormData.addressLine1?.trim()) {
            errors.addressLine1 = "Address Line 1 is mandatory";
        }
        if (!getFormData.country) {
            errors.country = "Country is mandatory";
        }
        if (!getFormData.state) {
            errors.state = "State is mandatory";
        }
        if (!getFormData.city?.trim()) {
            errors.city = "City is mandatory";
        }
        if (!getFormData.contactEmail?.trim()) {
            errors.contactEmail = "Contact Email is mandatory";
        }
        if (!getFormData.contactNumber?.trim()) {
            errors.contactNumber = "Contact Number is mandatory";
        }

        // Format validations
        if (getFormData.contactEmail?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(getFormData.contactEmail)) {
            errors.contactEmail = "Please enter a valid email address";
        }
        if (getFormData.pinCode?.trim() && !/^\d{6}$/.test(getFormData.pinCode)) {
            errors.pinCode = "PIN Code must be 6 digits";
        }
        if (getFormData.contactNumber?.trim() && !/^\d{10}$/.test(getFormData.contactNumber.replace(/\D/g, ""))) {
            errors.contactNumber = "Contact Number must be 10 digits";
        }
        if (getFormData.pan?.trim() && !/^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/.test(getFormData.pan)) {
            errors.pan = "Invalid PAN format";
        }
        if (getFormData.gstNumber?.trim() && !/^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz1-9A-J]{1}[0-9A-Za-z]{1}$/.test(getFormData.gstNumber)) {
            errors.gstNumber = "Invalid GSTIN format";
        }
        if (getFormData.budgetTolerancePercent !== "" &&
            (isNaN(getFormData.budgetTolerancePercent) ||
                Number(getFormData.budgetTolerancePercent) < 0 ||
                Number(getFormData.budgetTolerancePercent) > 100)) {
            errors.budgetTolerancePercent = "Tolerance % must be between 0 and 100";
        }

        // Map errors to their tabs so the tab headers highlight red
        Object.keys(errors).forEach((field) => {
            errorTabs[getTabOfField(field)] = true;
        });

        setErrorFields(Object.keys(errors));
        setTabErrors(errorTabs);
        return errors;
    };

    /* ---------------------------- Wizard navigation - same as CreateRFQTabs --------------------------------*/
    const currentTabIndex = companyTabList.findIndex((tab) => tab.id === activeTab);
    const isLastTab = currentTabIndex === companyTabList.length - 1;
    const goToPreviousTab = () => {
        if (currentTabIndex > 0) setActiveTab(companyTabList[currentTabIndex - 1].id);
    };
    const goToNextTab = () => {
        // Validate current tab's fields before moving forward
        const errors = validateForm();
        const currentTabFields = Object.keys(errors).filter((f) => getTabOfField(f) === activeTab);
        if (currentTabFields.length > 0) {
            toast.error("Please fill the required fields");
            return;
        }
        if (currentTabIndex < companyTabList.length - 1) {
            setActiveTab(companyTabList[currentTabIndex + 1].id);
        }
    };

    /* ---------------------------- Submit - Create / Update --------------------------------*/
    const handleCreateUpdateCompany = async () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            // Jump to the first tab that has an error
            const firstErrorTab = companyTabList.find((tab) => tabErrors[tab.id]);
            if (firstErrorTab) setActiveTab(firstErrorTab.id);
            toast.error("Please fill the required fields");
            return;
        }
        try {
            setIsLoading(true);
            let payload = { data: { ...getFormData } };

            if (isUpdateMode) {
                payload.data._id = companyId;
                await ItemService.updateCompany(payload).then((item) => {
                    if (item?.status === 200) {
                        toast.success(item?.message || "Company updated successfully");
                        navigate(Constants.COMPANY_CREATION_DETAILS, { state: { companyId: companyId } });
                    } else {
                        toast.error(item?.message || "!! Unable to update Company");
                    }
                });
            } else {
                await ItemService.addCompany(payload).then((item) => {
                    if (item?.status === 200) {
                        toast.success(item?.message || "Company created successfully");
                        navigate(Constants.COMPANY_CREATION_LIST);
                    } else {
                        toast.error(item?.message || "!! Unable to create Company");
                    }
                });
            }
        } catch (error) {
            console.log("Company Submit Failed:", error);
            toast.error("!! Unable to save Company");
        } finally {
            setIsLoading(false);
        }
    };

    /* ---------------------------- Render helpers --------------------------------*/
    const toDropdownValue = (val) => (val ? { id: val, name: val } : "");

    const getErrorMsg = (name, label) => {
        if (!getErrorFields.includes(name)) return "";
        if (name === "companyName") return "Company Name is mandatory";
        if (name === "companyType") return "Company Type is mandatory";
        if (name === "addressLine1") return "Address Line 1 is mandatory";
        if (name === "country") return "Country is mandatory";
        if (name === "state") return "State is mandatory";
        if (name === "city") return "City is mandatory";
        if (name === "contactEmail") return "Contact Email is mandatory";
        if (name === "contactNumber") return "Contact Number is mandatory";
        return `${label} is mandatory`;
    };

    return (
        <main className="dashboard main" id="main">
            <Layout />
            <Row className="justify-center subContent">
                <Col xs={12} md={12} className="rightSide">
                    <div className="rightSideInner PageCommonCSS">
                        <h2 className="mainTitle">
                            {isUpdateMode ? "Update Company" : "Company Creation"}
                        </h2>
                        <p className="subText">Fill Company Details</p>
                        <hr />

                        <div className="card">
                            <div className="card-body">
                                {/* Tab Headers - same pattern as CreateRFQTabs */}
                                <ul className="nav nav-tabs" id="myTab" role="tablist">
                                    {companyTabList.map((tab) => {
                                        const hasError = tabErrors[tab.id];
                                        const isActive = activeTab === tab.id;
                                        return (
                                            <li className="nav-item" role="presentation" key={tab.id}>
                                                <button
                                                    className={`nav-link ${isActive ? "active" : ""}`}
                                                    id={`${tab.id}-tab`}
                                                    type="button"
                                                    role="tab"
                                                    aria-controls={tab.id}
                                                    aria-selected={isActive ? "true" : "false"}
                                                    onClick={() => setActiveTab(tab.id)}
                                                    style={hasError ? { color: "#dc3545" } : undefined}
                                                >
                                                    {tab.label}
                                                    {hasError && (
                                                        <i
                                                            className="bi bi-exclamation-circle-fill"
                                                            style={{ marginLeft: "4px" }}
                                                        ></i>
                                                    )}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>

                                <div className="tab-content pt-2" id="myTabContent">
                                    {/* ---------------- Basic Information ---------------- */}
                                    <div
                                        className={`tab-pane fade ${activeTab === "basic-info" ? "show active" : ""}`}
                                        id="basic-info"
                                        role="tabpanel"
                                        aria-labelledby="basic-info-tab"
                                    >
                                        <div className="accordion mb-4" id="accordionExample">
                                            <div className="accordion-item">
                                                <h2 className="accordion-header">
                                                    <Row className="accordion-button">
                                                        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                                                            <i
                                                                className={`bi ${isCardVisible ? "bi-chevron-right" : "bi-chevron-down"}`}
                                                                onClick={toggleCardVisibility}
                                                            ></i>
                                                        </Col>
                                                        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                                                            <span className="iconLogo">BI</span>
                                                        </Col>
                                                        <Col xs={8} md={9} lg={9}>
                                                            <span className="headerTitle">Basic Information</span>
                                                            <span className="subHeaderTitle">
                                                                Company Identity & Status
                                                            </span>
                                                        </Col>
                                                    </Row>
                                                </h2>
                                                <div
                                                    className={`accordion-collapse collapse show ${isCardVisible ? "visible" : ""}`}
                                                >
                                                    <div className="accordion-body">
                                                        <Row className="pt-2 pb-4">
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Company Code"
                                                                    name="companyCode"
                                                                    id="companyCode"
                                                                    placeholder="Optional - auto COM0001 if blank"
                                                                    value={getFormData.companyCode || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Company Name"
                                                                    name="companyName"
                                                                    id="companyName"
                                                                    placeholder="e.g. Reliance Digital Limited"
                                                                    value={getFormData.companyName || ""}
                                                                    onChange={handleFieldChange}
                                                                    required={true}
                                                                    error={getErrorMsg("companyName", "Company Name")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Legal Name"
                                                                    name="legalName"
                                                                    id="legalName"
                                                                    placeholder="Registered legal entity name"
                                                                    value={getFormData.legalName || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Company Short Name"
                                                                    name="companyShortName"
                                                                    id="companyShortName"
                                                                    placeholder="e.g. Reliance Digital"
                                                                    value={getFormData.companyShortName || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Company Type"
                                                                    placeholder=""
                                                                    data={companyTypeOptions}
                                                                    id="companyType"
                                                                    name="companyType"
                                                                    setValue={toDropdownValue(getFormData.companyType)}
                                                                    getvalue={handleDropdownGetValue}
                                                                    required={true}
                                                                    error={getErrorMsg("companyType", "Company Type")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Registration Number"
                                                                    name="registrationNumber"
                                                                    id="registrationNumber"
                                                                    placeholder="CIN or applicable registration ID"
                                                                    value={getFormData.registrationNumber || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Company Status"
                                                                    placeholder=""
                                                                    data={companyStatusOptions}
                                                                    id="companyStatus"
                                                                    name="companyStatus"
                                                                    setValue={toDropdownValue(getFormData.companyStatus)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <FileUploadComponent
                                                                    id="companyLogo"
                                                                    name="companyLogo"
                                                                    label="Company Logo (Optional)"
                                                                    allowedTypes={[
                                                                        "image/png",
                                                                        "image/jpeg",
                                                                        "image/jpg",
                                                                        "application/pdf",
                                                                    ]}
                                                                    multiple={false}
                                                                    onSuccessUpload={handleLogoUpload}
                                                                    value={getFormData.companyLogo || ""}
                                                                />
                                                            </Col>
                                                            <Col
                                                                xs={12}
                                                                md={1}
                                                                lg={1}
                                                                className="commTopButtonRightLeftPadding d-flex align-items-center"
                                                            >
                                                                {(getFormData.companyLogo && (
                                                                    <img
                                                                        src="assets/img/newIcons/attached-file.png"
                                                                        className="mt-1 attachmentIcon"
                                                                        alt="attachments"
                                                                        onClick={() => handleViewFile(getFormData.companyLogo)}
                                                                    />
                                                                )) ||
                                                                    "--"}
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ---------------- Registered Address & Contact ---------------- */}
                                    <div
                                        className={`tab-pane fade ${activeTab === "address-contact" ? "show active" : ""}`}
                                        id="address-contact"
                                        role="tabpanel"
                                        aria-labelledby="address-contact-tab"
                                    >
                                        <div className="accordion mb-4" id="accordionExample">
                                            <div className="accordion-item">
                                                <h2 className="accordion-header">
                                                    <Row className="accordion-button">
                                                        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                                                            <i
                                                                className={`bi ${isCardVisible2 ? "bi-chevron-right" : "bi-chevron-down"}`}
                                                                onClick={toggleCardVisibility2}
                                                            ></i>
                                                        </Col>
                                                        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                                                            <span className="iconLogo">AC</span>
                                                        </Col>
                                                        <Col xs={8} md={9} lg={9}>
                                                            <span className="headerTitle">Registered Address & Contact</span>
                                                            <span className="subHeaderTitle">
                                                                Company Address & Official Contact
                                                            </span>
                                                        </Col>
                                                    </Row>
                                                </h2>
                                                <div
                                                    className={`accordion-collapse collapse show ${isCardVisible2 ? "visible" : ""}`}
                                                >
                                                    <div className="accordion-body">
                                                        <Row className="pt-2 pb-4">
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Address Line 1"
                                                                    name="addressLine1"
                                                                    id="addressLine1"
                                                                    placeholder="Registered address line 1"
                                                                    value={getFormData.addressLine1 || ""}
                                                                    onChange={handleFieldChange}
                                                                    required={true}
                                                                    error={getErrorMsg("addressLine1", "Address Line 1")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Address Line 2"
                                                                    name="addressLine2"
                                                                    id="addressLine2"
                                                                    placeholder="Registered address line 2"
                                                                    value={getFormData.addressLine2 || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Country"
                                                                    placeholder=""
                                                                    data={countryOptions}
                                                                    id="country"
                                                                    name="country"
                                                                    setValue={toDropdownValue(getFormData.country)}
                                                                    getvalue={handleDropdownGetValue}
                                                                    required={true}
                                                                    error={getErrorMsg("country", "Country")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="State"
                                                                    placeholder=""
                                                                    data={stateOptions}
                                                                    id="state"
                                                                    name="state"
                                                                    setValue={toDropdownValue(getFormData.state)}
                                                                    getvalue={handleDropdownGetValue}
                                                                    required={true}
                                                                    error={getErrorMsg("state", "State")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="City"
                                                                    name="city"
                                                                    id="city"
                                                                    placeholder="Registered city"
                                                                    value={getFormData.city || ""}
                                                                    onChange={handleFieldChange}
                                                                    required={true}
                                                                    error={getErrorMsg("city", "City")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="PIN Code"
                                                                    name="pinCode"
                                                                    id="pinCode"
                                                                    placeholder="Postal code"
                                                                    value={getFormData.pinCode || ""}
                                                                    onChange={handleFieldChange}
                                                                    maxLength={6}
                                                                    error={getErrorMsg("pinCode", "PIN Code")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Primary Contact Person"
                                                                    name="primaryContactPerson"
                                                                    id="primaryContactPerson"
                                                                    placeholder="Company representative"
                                                                    value={getFormData.primaryContactPerson || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Contact Email"
                                                                    name="contactEmail"
                                                                    id="contactEmail"
                                                                    placeholder="Official email"
                                                                    value={getFormData.contactEmail || ""}
                                                                    onChange={handleFieldChange}
                                                                    required={true}
                                                                    error={getErrorMsg("contactEmail", "Contact Email")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Contact Number"
                                                                    name="contactNumber"
                                                                    id="contactNumber"
                                                                    placeholder="Official phone"
                                                                    value={getFormData.contactNumber || ""}
                                                                    onChange={handleFieldChange}
                                                                    required={true}
                                                                    error={getErrorMsg("contactNumber", "Contact Number")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Website"
                                                                    name="website"
                                                                    id="website"
                                                                    placeholder="Optional"
                                                                    value={getFormData.website || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ---------------- Tax & Financial Details ---------------- */}
                                    <div
                                        className={`tab-pane fade ${activeTab === "tax-financial" ? "show active" : ""}`}
                                        id="tax-financial"
                                        role="tabpanel"
                                        aria-labelledby="tax-financial-tab"
                                    >
                                        <div className="accordion mb-4" id="accordionExample">
                                            <div className="accordion-item">
                                                <h2 className="accordion-header">
                                                    <Row className="accordion-button">
                                                        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                                                            <i
                                                                className={`bi ${isCardVisible3 ? "bi-chevron-right" : "bi-chevron-down"}`}
                                                                onClick={toggleCardVisibility3}
                                                            ></i>
                                                        </Col>
                                                        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                                                            <span className="iconLogo">TF</span>
                                                        </Col>
                                                        <Col xs={8} md={9} lg={9}>
                                                            <span className="headerTitle">Tax & Financial Details</span>
                                                            <span className="subHeaderTitle">
                                                                PAN, GST & ERP Information
                                                            </span>
                                                        </Col>
                                                    </Row>
                                                </h2>
                                                <div
                                                    className={`accordion-collapse collapse show ${isCardVisible3 ? "visible" : ""}`}
                                                >
                                                    <div className="accordion-body">
                                                        <Row className="pt-2 pb-4">
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="PAN"
                                                                    name="pan"
                                                                    id="pan"
                                                                    placeholder="Indian tax identifier"
                                                                    value={getFormData.pan || ""}
                                                                    onChange={handleFieldChange}
                                                                    error={getErrorMsg("pan", "PAN")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="GSTIN"
                                                                    name="gstNumber"
                                                                    id="gstNumber"
                                                                    placeholder="GST registration for the entity"
                                                                    value={getFormData.gstNumber || ""}
                                                                    onChange={handleFieldChange}
                                                                    error={getErrorMsg("gstNumber", "GSTIN")}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Currency"
                                                                    placeholder=""
                                                                    data={currencyOptions}
                                                                    id="currency"
                                                                    name="currency"
                                                                    setValue={toDropdownValue(getFormData.currency)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Financial Year Start"
                                                                    name="financialYearStart"
                                                                    id="financialYearStart"
                                                                    placeholder="e.g. 1 April"
                                                                    value={getFormData.financialYearStart || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Financial Year End"
                                                                    name="financialYearEnd"
                                                                    id="financialYearEnd"
                                                                    placeholder="e.g. 31 March"
                                                                    value={getFormData.financialYearEnd || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Accounting Period"
                                                                    placeholder=""
                                                                    data={accountingPeriodOptions}
                                                                    id="accountingPeriod"
                                                                    name="accountingPeriod"
                                                                    setValue={toDropdownValue(getFormData.accountingPeriod)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="ERP System"
                                                                    placeholder=""
                                                                    data={erpSystemOptions}
                                                                    id="erpSystem"
                                                                    name="erpSystem"
                                                                    setValue={toDropdownValue(getFormData.erpSystem)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="ERP Company/Entity ID"
                                                                    name="erpCompanyId"
                                                                    id="erpCompanyId"
                                                                    placeholder="Identifier in the connected ERP"
                                                                    value={getFormData.erpCompanyId || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ---------------- Budget Control Settings ---------------- */}
                                    <div
                                        className={`tab-pane fade ${activeTab === "budget-control" ? "show active" : ""}`}
                                        id="budget-control"
                                        role="tabpanel"
                                        aria-labelledby="budget-control-tab"
                                    >
                                        <div className="accordion mb-4" id="accordionExample">
                                            <div className="accordion-item">
                                                <h2 className="accordion-header">
                                                    <Row className="accordion-button">
                                                        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                                                            <i
                                                                className={`bi ${isCardVisible4 ? "bi-chevron-right" : "bi-chevron-down"}`}
                                                                onClick={toggleCardVisibility4}
                                                            ></i>
                                                        </Col>
                                                        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                                                            <span className="iconLogo">BC</span>
                                                        </Col>
                                                        <Col xs={8} md={9} lg={9}>
                                                            <span className="headerTitle">Budget Control Settings</span>
                                                            <span className="subHeaderTitle">
                                                                Budget Management Configuration
                                                            </span>
                                                        </Col>
                                                    </Row>
                                                </h2>
                                                <div
                                                    className={`accordion-collapse collapse show ${isCardVisible4 ? "visible" : ""}`}
                                                >
                                                    <div className="accordion-body">
                                                        <Row className="pt-2 pb-4">
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Budget Control"
                                                                    placeholder=""
                                                                    data={enabledDisabledOptions}
                                                                    id="budgetControl"
                                                                    name="budgetControl"
                                                                    setValue={toDropdownValue(getFormData.budgetControl)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Budget Type"
                                                                    placeholder=""
                                                                    data={budgetTypeOptions}
                                                                    id="budgetType"
                                                                    name="budgetType"
                                                                    setValue={toDropdownValue(getFormData.budgetType)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Budget Control Level"
                                                                    placeholder=""
                                                                    data={budgetControlLevelOptions}
                                                                    id="budgetControlLevel"
                                                                    name="budgetControlLevel"
                                                                    setValue={toDropdownValue(getFormData.budgetControlLevel)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Budget Check Basis"
                                                                    placeholder=""
                                                                    data={budgetCheckBasisOptions}
                                                                    id="budgetCheckBasis"
                                                                    name="budgetCheckBasis"
                                                                    setValue={toDropdownValue(getFormData.budgetCheckBasis)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Budget Exceed Action"
                                                                    placeholder=""
                                                                    data={budgetExceedActionOptions}
                                                                    id="budgetExceedAction"
                                                                    name="budgetExceedAction"
                                                                    setValue={toDropdownValue(getFormData.budgetExceedAction)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Budget Carry Forward"
                                                                    placeholder=""
                                                                    data={enabledDisabledOptions}
                                                                    id="budgetCarryForward"
                                                                    name="budgetCarryForward"
                                                                    setValue={toDropdownValue(getFormData.budgetCarryForward)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <ComboboxField
                                                                    label="Budget Approval Workflow"
                                                                    placeholder=""
                                                                    data={budgetApprovalWorkflowOptions}
                                                                    id="budgetApprovalWorkflow"
                                                                    name="budgetApprovalWorkflow"
                                                                    setValue={toDropdownValue(getFormData.budgetApprovalWorkflow)}
                                                                    getvalue={handleDropdownGetValue}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Default Budget Owner"
                                                                    name="defaultBudgetOwner"
                                                                    id="defaultBudgetOwner"
                                                                    placeholder="Responsible user or role"
                                                                    value={getFormData.defaultBudgetOwner || ""}
                                                                    onChange={handleFieldChange}
                                                                />
                                                            </Col>
                                                            <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                                                                <InputField
                                                                    className="inputBox"
                                                                    label="Budget Tolerance (%)"
                                                                    name="budgetTolerancePercent"
                                                                    id="budgetTolerancePercent"
                                                                    type="number"
                                                                    placeholder="Configurable percentage"
                                                                    value={getFormData.budgetTolerancePercent || ""}
                                                                    onChange={handleFieldChange}
                                                                    error={getErrorMsg("budgetTolerancePercent", "Budget Tolerance")}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Page Footer - Previous / Next / Create - same as CreateRFQTabs */}
            <div className="pageFooter mb-4 pb-2">
                <Row className="align-items-center">
                    <Col xs={12} md={3} lg={3} className="mb-2">
                        {currentTabIndex > 0 && (
                            <button
                                type="button"
                                className="btnTable"
                                onClick={goToPreviousTab}
                            >
                                <i className="bi bi-arrow-left me-2"></i> Previous
                            </button>
                        )}
                    </Col>
                    <Col xs={12} md={3} lg={3} className="mb-2 ms-auto text-end">
                        {isLastTab ? (
                            <button
                                type="button"
                                disabled={getIsLoading}
                                className="btnTable"
                                onClick={handleCreateUpdateCompany}
                            >
                                <i className="bi bi-check2-circle me-2"></i>
                                {isUpdateMode ? "Update Company" : "Create Company"}
                            </button>
                        ) : (
                            <button type="button" className="btnTable" onClick={goToNextTab}>
                                Next <i className="bi bi-arrow-right ms-2"></i>
                            </button>
                        )}
                    </Col>
                </Row>
            </div>

            <FileViewerModal
                show={getShowFileViewModal}
                onHide={handleViewFileHide}
                fileid={getFileViewData}
            />
        </main>
    );
};

export default CompanyCreation;
